<template>
  <div class="user-dropdown" ref="dropdownRef">
    <button class="avatar-button" @click="toggleDropdown">
      <img
        v-if="photoURL"
        :src="photoURL"
        :alt="displayName"
        class="avatar-image"
      />
      <span v-else class="avatar-initials">{{ initials }}</span>
    </button>

    <div v-if="isOpen" class="dropdown-menu">
      <div class="user-info">
        <span class="user-name">{{ displayName }}</span>
        <span class="user-email">{{ email }}</span>
      </div>
      <button v-if="isPremium" @click="manageSubscription" class="dropdown-item">
        Manage Subscription
      </button>
      <button v-else @click="handleUpgrade" class="dropdown-item upgrade-item">
        Upgrade to Premium
      </button>
      <div class="dropdown-divider"></div>
      <button @click="handleSignOut" class="sign-out-button">
        Sign out
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useSubscription } from '../composables/useSubscription'

export default {
  name: 'UserDropdown',
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  emits: ['sign-out'],
  setup(props, { emit }) {
    const isOpen = ref(false)
    const dropdownRef = ref(null)
    const { isPremium, manageSubscription, goToPremium } = useSubscription()

    const displayName = computed(() => {
      return props.user.displayName || props.user.email?.split('@')[0] || 'User'
    })

    const email = computed(() => props.user.email || '')

    const photoURL = computed(() => props.user.photoURL || null)

    const initials = computed(() => {
      const name = displayName.value
      if (!name) return '?'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    })

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value
    }

    const handleSignOut = () => {
      isOpen.value = false
      emit('sign-out')
    }

    const handleUpgrade = () => {
      isOpen.value = false
      goToPremium()
    }

    const handleClickOutside = (event) => {
      if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
        isOpen.value = false
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      isOpen,
      dropdownRef,
      displayName,
      email,
      photoURL,
      initials,
      toggleDropdown,
      handleSignOut,
      isPremium,
      manageSubscription,
      handleUpgrade
    }
  }
}
</script>

<style scoped>
.user-dropdown {
  position: relative;
}

.avatar-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid rgba(255, 165, 0, 0.5);
  background: rgba(0, 0, 0, 0.3);
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s;
}

.avatar-button:hover {
  border-color: #ffa500;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  color: #ffa500;
  font-size: 0.875rem;
  font-weight: 600;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: rgba(26, 26, 26, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  min-width: 200px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 1000;
}

.user-info {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.user-name {
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
}

.user-email {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
}

.dropdown-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
}

.dropdown-item {
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  text-align: left;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.upgrade-item {
  color: #ffa500;
  font-weight: 500;
}

.upgrade-item:hover {
  background: rgba(255, 165, 0, 0.1);
  color: #ffa500;
}

.sign-out-button {
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  text-align: left;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.2s;
}

.sign-out-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}
</style>
